import * as React from "react";
import { SpsOptionListOption } from "@spscommerce/ds-shared";
import { debounce } from "@spscommerce/utils";
import { SpsOptionListProps } from "./SpsOptionListProps";
import { OptionListSearchState } from "./useOptionListSearch";

async function updateOptions(
    props: SpsOptionListProps,
    searchState: OptionListSearchState,
    searchStatePatch: React.Dispatch<Partial<OptionListSearchState>>,
    setOptionList: React.Dispatch<React.SetStateAction<readonly SpsOptionListOption<any>[]>>,
    setAnyOptionHasIcon: React.Dispatch<React.SetStateAction<boolean>>,
) {
    searchStatePatch({ pending: true });

    const fetchedOptions = typeof props.options === "function"
        ? props.options(searchState.value)
        : props.options || [];
    const resultPromise = fetchedOptions instanceof Promise
        ? fetchedOptions
        : Promise.resolve(fetchedOptions);

    const result = await resultPromise || [];
    let newOpts = Array.from(result)
        .filter(Boolean)
        .map(
            thing => new SpsOptionListOption(
                thing,
                typeof thing === "function"
                ? {
                    textKey: "label",
                    captionKey: "caption"
                }
                : {
                    textKey: props.textKey,
                    captionKey: props.captionKey
                }
            )
        );

    if (props.nullOption) {
        newOpts.unshift(new SpsOptionListOption<null>(null, { text: props.nullOption }));
    }

    if (searchState.value) {
        searchStatePatch({ replacementPattern: new RegExp(searchState.value, "ig") });
        newOpts = newOpts.filter(o => (new RegExp(searchState.value, "i")).test(o.text));
    } else {
        searchStatePatch({ replacementPattern: null });
    }

    setOptionList(Object.freeze(newOpts));
    if (props.onOptionListChanged) {
        props.onOptionListChanged(newOpts.length);
    }
    setAnyOptionHasIcon(newOpts.some(o => o.value && !!o.value["icon"]));
    searchStatePatch({ pending: false });
}

export function useOptionListOptions(
    props: SpsOptionListProps,
    searchState: OptionListSearchState,
    searchStatePatch: React.Dispatch<Partial<OptionListSearchState>>,
): [
    readonly SpsOptionListOption<any>[],
    boolean
] {
    const [optionList, setOptionList] = React.useState<readonly SpsOptionListOption<any>[]>(Object.freeze([]));
    const [anyOptionHasIcon, setAnyOptionHasIcon] = React.useState(false);

    const update = React.useMemo(
        () => typeof props.options === "function"
            ? debounce(updateOptions, typeof props.searchDebounce !== "undefined" ? props.searchDebounce : 500)
            : updateOptions,
        [props.options]
    );

    React.useEffect(() => {
        update(props, searchState, searchStatePatch, setOptionList, setAnyOptionHasIcon);
    }, [props.options, searchState.value]);

    return [optionList, anyOptionHasIcon];
}
